// Expéditeur — what the recipient sees as the SMS author: Unanimity's sender
// (nothing to provide) or the company's own name, which OVH validates on
// documents (Kbis + legal representative's ID). The documents go straight to
// OVH; Unanimity keeps only their name and size.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBuilding, FaCheckCircle, FaClock, FaExclamationTriangle, FaFileAlt, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { del, fail, formatBytes, formatWhen, post, postForm } from './api';

const SENDER_RE = /^[A-Za-z0-9][A-Za-z0-9 .-]{1,9}[A-Za-z0-9]$/;
const DOCS = [
    { key: 'kbis', required: true },
    { key: 'id_front', required: true },
    { key: 'id_back', required: false },
];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const PlatformStatus = ({ status, t }) => {
    if (status === 'enable') return <span className="csms-chip csms-chip--success">{t('customerSms.sender.active', 'Actif')}</span>;
    if (!status) return <span className="csms-chip csms-chip--muted">{t('customerSms.sender.checking', 'Vérification en cours')}</span>;
    return <span className="csms-chip csms-chip--warning">{t('customerSms.sender.platformPending', 'Pas encore actif')}</span>;
};

const OwnStatus = ({ own, t }) => {
    if (!own) return <span className="csms-chip csms-chip--muted">{t('customerSms.sender.notRequested', 'Non demandé')}</span>;
    const tone = { pending: 'warning', active: 'success', refused: 'danger', disabled: 'danger', submitting: 'muted' }[own.status] || 'muted';
    return <span className={`csms-chip csms-chip--${tone}`}>{t(`customerSms.sender.status.${own.status}`, own.status)}</span>;
};

const RequestForm = ({ defaults, limits, onDone, setFeedback, t }) => {
    const [name, setName] = useState(defaults.name || '');
    const [company, setCompany] = useState(defaults.company_name || '');
    const [siren, setSiren] = useState(defaults.siren || '');
    const [files, setFiles] = useState({});
    const [attest, setAttest] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const trimmed = name.trim().replace(/\s+/g, ' ');
    const nameOk = SENDER_RE.test(trimmed) && /[A-Za-z]/.test(trimmed);
    const sirenOk = /^\d{9}$|^\d{14}$/.test(siren.replace(/\s/g, ''));
    const tooBig = Object.values(files).some((f) => f && f.size > (limits?.document_max_bytes || 8 * 1024 * 1024));
    const ready = nameOk && company.trim().length > 1 && sirenOk && files.kbis && files.id_front && attest && !tooBig;

    const pick = (key) => (e) => {
        const file = e.target.files?.[0];
        setFiles((prev) => ({ ...prev, [key]: file || undefined }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!ready) return;
        const form = new FormData();
        form.append('name', trimmed);
        form.append('company_name', company.trim());
        form.append('siren', siren.replace(/\s/g, ''));
        form.append('attest', 'true');
        DOCS.forEach(({ key }) => { if (files[key]) form.append(key, files[key]); });
        setBusy(true);
        setError('');
        try {
            const res = await postForm('/sender', form);
            setFeedback({ tone: 'success', text: t('customerSms.sender.sent', 'Demande envoyée à OVH. Vous serez prévenu dès sa validation.') });
            onDone(res.data);
        } catch (err) {
            setError(fail(err, t('customerSms.sender.sendError', "La demande n'a pas pu être envoyée.")));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="csms-request" onSubmit={submit}>
            <div className="csms-grid">
                <label className="csms-field">
                    <span>{t('customerSms.sender.name', "Nom d'expéditeur")}</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} maxLength={11} placeholder="LOGVAD"
                        aria-invalid={name !== '' && !nameOk} />
                    <small className={`csms-field-hint ${name !== '' && !nameOk ? 'csms-error-text' : ''}`}>
                        <span>{t('customerSms.sender.nameHint', '3 à 11 caractères : lettres, chiffres, espace, point ou tiret.')}</span>
                        <span className="csms-counter">{trimmed.length}/11</span>
                    </small>
                </label>
                <label className="csms-field">
                    <span>{t('customerSms.sender.company', 'Raison sociale')}</span>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120} placeholder="LogVAD SAS" />
                </label>
                <label className="csms-field">
                    <span>{t('customerSms.sender.siren', 'SIREN ou SIRET')}</span>
                    <input value={siren} onChange={(e) => setSiren(e.target.value)} maxLength={20} inputMode="numeric"
                        placeholder="732 829 320" aria-invalid={siren !== '' && !sirenOk} />
                </label>
            </div>

            <div className="csms-docs">
                {DOCS.map(({ key, required }) => (
                    <label key={key} className={`csms-doc ${files[key] ? 'csms-doc--set' : ''}`}>
                        <input type="file" accept={ACCEPT} onChange={pick(key)} />
                        <FaFileAlt aria-hidden="true" />
                        <span className="csms-doc-text">
                            <strong>{t(`customerSms.sender.docs.${key}`, key)}{required ? '' : ` ${t('customerSms.optional', '(facultatif)')}`}</strong>
                            <small>
                                {files[key]
                                    ? `${files[key].name} · ${formatBytes(files[key].size)}`
                                    : t(`customerSms.sender.docsHint.${key}`, 'PDF, JPEG ou PNG · 8 Mo max.')}
                            </small>
                        </span>
                    </label>
                ))}
            </div>
            {tooBig && <p className="csms-error-text">{t('customerSms.sender.tooBig', 'Un fichier dépasse 8 Mo.')}</p>}

            <label className="csms-check">
                <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
                <span>{t('customerSms.sender.attest', "J'atteste être habilité à représenter cette entreprise et à utiliser ce nom d'expéditeur.")}</span>
            </label>
            <p className="csms-note">
                {t('customerSms.sender.privacy', "Les documents sont transmis directement à OVH pour la validation et ne sont pas conservés par Unanimity.")}
            </p>
            {error && <p className="csms-error-text" role="alert">{error}</p>}
            <div className="csms-actions">
                <button type="submit" className="u-btn u-btn--primary" disabled={!ready || busy}>
                    {busy ? t('customerSms.sender.sending', 'Envoi à OVH…') : t('customerSms.sender.submit', 'Envoyer la demande à OVH')}
                </button>
            </div>
        </form>
    );
};

const OwnProgress = ({ own, lang, canEdit, onChange, setFeedback, t }) => {
    const [busy, setBusy] = useState(false);
    const refresh = async () => {
        setBusy(true);
        try {
            const res = await post('/sender/refresh');
            onChange(res.data);
        } catch (err) {
            setFeedback({ tone: 'danger', text: fail(err, t('customerSms.sender.refreshError', 'OVH ne répond pas pour le moment.')) });
        } finally {
            setBusy(false);
        }
    };
    const [confirming, setConfirming] = useState(false);
    const withdraw = async () => {
        setBusy(true);
        try {
            const res = await del('/sender');
            setFeedback({ tone: 'success', text: t('customerSms.sender.withdrawn', 'Expéditeur retiré : vos SMS partent sous « Unanimity ».') });
            onChange(res.data);
        } catch (err) {
            setFeedback({ tone: 'danger', text: fail(err, t('customerSms.sender.withdrawError', "Le retrait n'a pas abouti.")) });
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };
    const steps = [
        { key: 'sent', done: true, label: t('customerSms.sender.stepSent', 'Demande envoyée'), at: own.submitted_at },
        { key: 'review', done: own.status === 'active', current: own.status === 'pending',
          label: t('customerSms.sender.stepReview', 'Validation par OVH'),
          sub: own.status === 'pending' ? t('customerSms.sender.reviewDelay', 'En général 2 à 5 jours ouvrés') : null },
        { key: 'active', done: own.status === 'active', label: t('customerSms.sender.stepActive', 'Expéditeur actif') },
    ];
    return (
        <div className="csms-progress">
            <ol className="csms-steps">
                {steps.map((step) => (
                    <li key={step.key} className={`csms-step ${step.done ? 'csms-step--done' : ''} ${step.current ? 'csms-step--current' : ''}`}>
                        <span className="csms-step-dot" aria-hidden="true">{step.done ? <FaCheckCircle /> : step.current ? <FaClock /> : null}</span>
                        <span className="csms-step-text">
                            <strong>{step.label}</strong>
                            {step.at && <small>{formatWhen(step.at, lang)}</small>}
                            {step.sub && <small>{step.sub}</small>}
                        </span>
                    </li>
                ))}
            </ol>
            <dl className="csms-facts">
                <div><dt>{t('customerSms.sender.name', "Nom d'expéditeur")}</dt><dd className="csms-mono">{own.name}</dd></div>
                <div><dt>{t('customerSms.sender.company', 'Raison sociale')}</dt><dd>{own.company_name} · SIREN {own.siren}</dd></div>
                <div><dt>{t('customerSms.sender.documents', 'Justificatifs')}</dt>
                    <dd>{(own.documents || []).map((d) => t(`customerSms.sender.docs.${d.kind}`, d.kind)).join(' · ') || '—'}</dd></div>
                {own.checked_at && <div><dt>{t('customerSms.sender.checked', 'Dernière vérification')}</dt><dd>{formatWhen(own.checked_at, lang)}</dd></div>}
            </dl>
            {canEdit && (
                <div className="csms-actions">
                    {own.status === 'pending' && (
                        <button type="button" className="u-btn u-btn--secondary" onClick={refresh} disabled={busy}>
                            <FaSyncAlt /> {t('customerSms.sender.refresh', 'Vérifier maintenant')}
                        </button>
                    )}
                    {confirming ? (
                        <span className="csms-confirm">
                            {t('customerSms.sender.withdrawConfirm', "Supprimer l'expéditeur chez OVH ?")}
                            <button type="button" className="csms-link csms-link--danger" onClick={withdraw} disabled={busy}>
                                {t('customerSms.sender.withdrawYes', 'Oui, retirer')}
                            </button>
                            <button type="button" className="csms-link" onClick={() => setConfirming(false)}>{t('customerSms.cancel', 'Annuler')}</button>
                        </span>
                    ) : (
                        <button type="button" className="csms-link csms-link--danger" onClick={() => setConfirming(true)} disabled={busy}>
                            <FaTimes /> {own.status === 'active' ? t('customerSms.sender.remove', "Retirer l'expéditeur") : t('customerSms.sender.withdraw', 'Retirer la demande')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const SenderSection = ({ view, senderMode, onModeChange, onViewChange, setFeedback, lang }) => {
    const { t } = useTranslation();
    const own = view.sender.own;
    const platform = view.sender.platform;
    const canEdit = view.can_edit;
    const requestable = !own || ['refused', 'disabled'].includes(own.status);
    const [requesting, setRequesting] = useState(false);
    const ownSelected = senderMode === 'own' || (requesting && requestable);

    const choose = (mode) => {
        if (!canEdit) return;
        if (mode === 'own' && requestable) {
            setRequesting(true);
            return;
        }
        setRequesting(false);
        onModeChange(mode);
    };

    return (
        <section className="csms-card" aria-labelledby="csms-sender-title">
            <header className="csms-card-header">
                <div>
                    <h2 id="csms-sender-title">{t('customerSms.sender.title', 'Expéditeur')}</h2>
                    <p>{t('customerSms.sender.subtitle', 'Le nom affiché en haut du SMS chez vos destinataires.')}</p>
                </div>
            </header>
            <div className="csms-options" role="radiogroup" aria-label={t('customerSms.sender.title', 'Expéditeur')}>
                <button type="button" role="radio" aria-checked={!ownSelected} disabled={!canEdit}
                    className={`csms-option ${!ownSelected ? 'csms-option--selected' : ''}`} onClick={() => choose('platform')}>
                    <span className="csms-option-head">
                        <span className="csms-radio" aria-hidden="true" />
                        <strong>{platform.name}</strong>
                        <PlatformStatus status={platform.status} t={t} />
                    </span>
                    <span className="csms-option-body">
                        {t('customerSms.sender.platformDesc', 'Vos SMS sont signés « {{name}} ». Rien à fournir, disponible tout de suite.', { name: platform.name })}
                    </span>
                </button>
                <button type="button" role="radio" aria-checked={ownSelected} disabled={!canEdit}
                    className={`csms-option ${ownSelected ? 'csms-option--selected' : ''}`} onClick={() => choose('own')}>
                    <span className="csms-option-head">
                        <span className="csms-radio" aria-hidden="true" />
                        <strong><FaBuilding aria-hidden="true" /> {own?.name || t('customerSms.sender.ownTitle', 'Votre entreprise')}</strong>
                        <OwnStatus own={own} t={t} />
                    </span>
                    <span className="csms-option-body">
                        {t('customerSms.sender.ownDesc', "Vos SMS portent le nom de votre entreprise. OVH le valide sur justificatifs : extrait Kbis de moins de 3 mois et pièce d'identité du représentant légal.")}
                    </span>
                </button>
            </div>

            {ownSelected && own?.status === 'refused' && (
                <div className="csms-callout csms-callout--danger" role="status">
                    <FaExclamationTriangle aria-hidden="true" />
                    <span>
                        <strong>{t('customerSms.sender.refusedTitle', 'OVH a refusé la demande')}</strong>
                        {own.moderator_comment && <> — {own.moderator_comment}</>}
                        <small>{t('customerSms.sender.refusedHint', 'Corrigez le document concerné et envoyez une nouvelle demande.')}</small>
                    </span>
                </div>
            )}
            {ownSelected && own?.status === 'disabled' && (
                <div className="csms-callout csms-callout--danger" role="status">
                    <FaExclamationTriangle aria-hidden="true" />
                    <span>{t('customerSms.sender.disabledHint', "OVH a désactivé cet expéditeur. Envoyez une nouvelle demande ou revenez à « {{name}} ».", { name: platform.name })}</span>
                </div>
            )}
            {ownSelected && own && ['pending', 'active'].includes(own.status) && (
                <>
                    {own.status === 'pending' && (
                        <div className="csms-callout csms-callout--info" role="status">
                            <FaClock aria-hidden="true" />
                            <span>{t('customerSms.sender.fallback', 'En attendant la validation, vos SMS partent sous « {{name}} ».', { name: platform.name })}</span>
                        </div>
                    )}
                    <OwnProgress own={own} lang={lang} canEdit={canEdit} onChange={onViewChange} setFeedback={setFeedback} t={t} />
                </>
            )}
            {ownSelected && requestable && canEdit && (
                <RequestForm defaults={own || {}} limits={view.limits} setFeedback={setFeedback} t={t}
                    onDone={(next) => { setRequesting(false); onViewChange(next); }} />
            )}
        </section>
    );
};

export default SenderSection;
